import React, { useState } from 'react'
import { useSelector, useDispatch, useStore } from 'react-redux'

import { makeStyles } from '@material-ui/core/styles'

import Grid from '@material-ui/core/Grid'
import TextField from '@material-ui/core/TextField'

import SimpleTable from 'components/table/SimpleTable'
import SimpleTableDeleteDialog from 'components/table/SimpleTableDeleteDialog'
import SimpleTableHeader from 'components/table/SimpleTableHeader'
import SimpleTableActions from 'components/table/SimpleTableActions'

import MenuButton from 'components/layout/MenuButton'
import MenuButtonItems from 'components/layout/MenuButtonItems'

import ResourceIconTitle from 'components/resource/ResourceIconTitle'
import UnitSelect from 'components/resource/UnitSelect'
import PriceSummary from 'components/resource/PriceSummary'
import LinkInfo from 'components/resource/LinkInfo'
import FolderPathTitle from 'components/resource/FolderPathTitle'

import settings from 'settings'
import selectors from 'store/selectors'
import routerActions from 'store/modules/router'
import networkActions from 'store/modules/network'
import resourceActions from 'store/modules/resource'

const {
  resources,
  units,
} = settings

const AddIcon = settings.icons.add
const EditIcon = settings.icons.edit
const DeleteIcon = settings.icons.delete
const ForwardIcon = settings.icons.forward
const MoreIcon = settings.icons.more

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  header: {
    width: '100%',
    flexGrow: 0,
  },
  content: {
    width: '100%',
    marginTop: theme.spacing(2),
    borderTop: '1px solid #ccc',
    flexGrow: 1,
    overflowY: 'auto',
  },
  iconText: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  iconTextIcon: {
    marginRight: theme.spacing(),
  },
  linkContainer: {
    cursor: 'pointer',
  },
  linkTitle: {
    textDecoration: 'underline',
    color: theme.palette.primary.main,
  },
  folderPathSplitter: {
    marginLeft: theme.spacing(),
    marginRight: theme.spacing(),
  },
  searchGridContainer: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  searchTextField: {
    width: '100%',
    marginTop: '0px',
    marginBottom: '0px',
  },
  timeUnitContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
  },
  timeUnitTextField: {
    width: '200px',
    marginRight: theme.spacing(),
    marginTop: '0px',
    marginBottom: '0px',
  },
  timeUnitSelect: {
    width: '200px',
  },
  priceCell: {
    width: '300px',
    minWidth: '300px',
    maxWidth: '300px',
  },
  mainButtons: {
    display: 'flex',
    flexDirection: 'row',
  },
  mainButtonsSplitter: {
    width: theme.spacing(),
  },
}))

const ResourceList = ({

}) => {

  const store = useStore()
  const classes = useStyles()
  const dispatch = useDispatch()

  const [ deleteConfirmOpen, setDeleteConfirmOpen ] = useState(false)
  const [ deleteConfirmItem, setDeleteConfirmItem ] = useState(null)
  const [ optionAnchorElementId, setOptionAnchorElementId ] = useState(null)
  const [ optionAnchorElement, setOptionAnchorElement ] = useState(null)

  const {
    section,
    parentid,
  } = useSelector(selectors.router.params)

  const parentItem = parentid == 'root' ?
    null :
    selectors.resource.treeCollection.get(store.getState(), parentid)

  const folderPath = useSelector(selectors.resource.folderPath)
  const currency = useSelector(selectors.project.currency)
  const items = useSelector(selectors.resource.listCollection.list)
  const search = useSelector(selectors.resource.search)
  const displayAmount = useSelector(selectors.resource.displayAmount)
  const displayTimeUnit = useSelector(selectors.resource.displayTimeUnit)
  const clipboard = useSelector(selectors.resource.clipboard)

  const openDeleteDialog = (item) => {
    setDeleteConfirmOpen(true)
    setDeleteConfirmItem(item)
  }

  const closeDeleteDialog = () => {
    setDeleteConfirmOpen(false)
    setDeleteConfirmItem(null)
  }

  const onAdd = ({
    section,
    parentid,
    type,
  }) => dispatch(routerActions.navigateTo('resource.edit', { section, parentid, type, id: 'new' }))
  
  const onEdit = ({
    section,
    parentid,
    type,
    id,
  }) => dispatch(routerActions.navigateTo('resource.edit', { section, parentid, type, id }))
  
  const onView = ({
    section,
    parentid,
  }) => dispatch(routerActions.navigateTo('resource.list', { section, parentid }))

  const onDelete = (payload) => dispatch(resourceActions.delete(payload))
  const onDuplicate = (payload) => dispatch(resourceActions.duplicate(payload))
  const onCut = (payload) => dispatch(resourceActions.cut(payload))
  const onCopy = (payload) => dispatch(resourceActions.copy(payload))
  const onPaste = (payload) => dispatch(resourceActions.paste(payload))
  const onUpdateSearch = (payload) => dispatch(resourceActions.updateSearch(payload))
  const onUpdateDisplayAmount = (payload) => dispatch(resourceActions.setDisplayAmount(payload))
  const onUpdateDisplayTimeUnit = (payload) => dispatch(resourceActions.setDisplayTimeUnit(payload))

  const resource = resources[section]
  const ResourceIcon = resource.icon
  const FolderIcon = resources.folder.icon

  const fields =[{
    title: 'Name',
    name: 'name',
    width: '30%',
  }, {
    title: 'Price',
    name: 'price',
    className: classes.priceCell,
    width: '25%',
  }, {
    title: 'Info',
    name: 'info',
    width: '25%',
  }]

  const folders = items.filter(item => item.type == 'folder')
  const nonFolders = items.filter(item => item.type != 'folder')

  const sortedItems = folders.concat(nonFolders)

  const data = sortedItems.map((resource, index) => {
    return {
      id: resource.id,
      type: resource.type,
      rawName: resource.name,
      resource: resource,
      name: (
        <ResourceIconTitle
          resource={ resource }
          className={ classes.linkContainer }
          textClassname={ classes.linkTitle }
          onClick={ 
            resource.type == 'folder' ?
              () => onView({
                section,
                parentid: resource.id,
              }) :
              () => onEdit({
                section,
                parentid: resource.parent || 'root',
                type: resource.type,
                id: resource.id,
              })
          }
        />
      ),
      price: (
        <PriceSummary
          currency={ currency }
          resource={ resource }
          amount={ displayAmount }
          unit={ displayTimeUnit }
        />
      ),
      info: (
        <LinkInfo
          currency={ currency }
          resource={ resource }
          amount={ displayAmount }
        />
      )
    }
  })

  const addButton = (
    <MenuButton 
      title="Add"
      icon={ AddIcon }
      buttonProps={{
        variant: "contained",
        color: "secondary"
      }}
      items={[{
        title: resource.title,
        icon: ResourceIcon,
        handler: () => onAdd({
          parentid,
          section,
          type: section,
        })
      },{
        title: 'Folder',
        icon: FolderIcon,
        handler: () => onAdd({
          parentid,
          section,
          type: 'folder',
        })
      }]}
    />
  )

  const optionsButton = (
    <MenuButton 
      title="Options"
      icon={ MoreIcon }
      items={[{
        title: 'Paste',
        disabled: clipboard.resource == null,
        handler: () => onPaste({
          section,
          parentid,
        })
      }]}
    />
  )

  const getActions = (item) => {
    let actions = []

    if(item.type == 'folder') {
      actions.push({
        title: 'View',
        icon: ForwardIcon,
        handler: (item) => onView({
          section,
          parentid: item.id,
        }),
      })
    }

    actions = actions.concat([{
      title: 'Options',
      icon: MoreIcon,
      handler: (item, event) => {
        setOptionAnchorElementId(item.id)
        setOptionAnchorElement(event.currentTarget)
      }
    },{
      title: 'Delete',
      icon: DeleteIcon,
      handler: (item) => openDeleteDialog(item),
    }, {
      title: 'Edit',
      icon: EditIcon,
      handler: (item) => onEdit({
        section,
        parentid,
        type: item.type,
        id: item.id,
      })
    }])

    const anchorEl = optionAnchorElementId == item.id ?
      optionAnchorElement :
      null

    const options = [{
      title: 'Duplicate',
      handler: () => onDuplicate({
        section,
        parentid,
        resource: item.resource,
      })
    }, {
      title: 'Cut',
      handler: () => onCut({
        resource: item.resource,
      })
    }, {
      title: 'Copy',
      handler: () => onCopy({
        resource: item.resource,
      })
    }]

    return (
      <SimpleTableActions
        item={ item }
        actions={ actions }
      >
        <MenuButtonItems
          anchorEl={ anchorEl }
          onClose={ () => {
            setOptionAnchorElement(null)
            setOptionAnchorElementId(null)
          } }
          items={ options }
        />
      </SimpleTableActions>
    )
  }

  return (
    <div className={ classes.root }>
      <div className={ classes.header }>
        <SimpleTableHeader
          title={(
            <FolderPathTitle
              section={ section }
              folderPath={ folderPath }
              search={ search }
              onView={ onView }
            />
          )}
          getActions={ () => (
            <div className={ classes.mainButtons }>
              { optionsButton }
              <div className={ classes.mainButtonsSplitter }></div>
              { addButton }
            </div>
          ) }
        />
        <div className={ classes.searchGridContainer }>
          <Grid container spacing={ 0 }>
            <Grid item xs={ 4 }>
              <TextField
                className={ classes.searchTextField }
                id="search"
                label="Search"
                value={ search }
                onChange={ (e) => onUpdateSearch({
                  section,
                  parentid,
                  search: e.target.value,
                }) }
                margin="normal"
              />
            </Grid>
            <Grid item xs={ 4 }></Grid>
            <Grid item xs={ 4 } className={ classes.timeUnitContainer }>
              <TextField
                className={ classes.timeUnitTextField }
                id="display-price-units"
                label="Show prices for amount:"
                value={ displayAmount }
                type="number"
                onChange={ (e) => onUpdateDisplayAmount(e.target.value) }
                margin="normal"
              />
              <UnitSelect
                type="time"
                title="Time in unit:"
                value={ displayTimeUnit }
                onChange={ onUpdateDisplayTimeUnit }
                className={ classes.timeUnitSelect }
              />
            </Grid>
          </Grid>
        </div>
      </div>
      <div className={ classes.content }>
        <SimpleTable
          data={ data }
          fields={ fields }
          getActions={ getActions }
        />
      </div>
      <SimpleTableDeleteDialog
        open={ deleteConfirmOpen }
        title={ deleteConfirmItem ? `the ${deleteConfirmItem.rawName} resource` : null }
        onCancel={ closeDeleteDialog }
        onConfirm={ () => {
          closeDeleteDialog()
          onDelete({
            section,
            parentid,
            id: deleteConfirmItem.id,
          })
        }}
      />
    </div>
  )
}

export default ResourceList

